import React from 'react';
import {
  Box,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useLocation, useNavigate } from 'react-router';

import UserBlock from '../Layout/UserBlock';
import { colors } from '../../theme/tokens';

import { SIDEBAR_GROUPS_ADMIN, AdminSidebarItem } from './sidebar/groupsConfig';


interface Props {
  onClose?: () => void;
}

const ACTIVE_BG = colors.adminAccentSubtle;
const ACTIVE_BORDER = colors.adminAccent;

const isActive = (pathname: string, path?: string): boolean => {
  if (!path) return false;
  return pathname === path || pathname.startsWith(`${path}/`);
};

/**
 * Admin sidebar. Same drawer scaffolding as the tenant sidebar, but
 * instead of `BrandHeader` we render a slate band titled "Platform
 * Admin · {env} · super-admin" per Designer A's brief.
 */
const AdminSidebar: React.FC<Props> = ({ onClose }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleClick = (item: AdminSidebarItem) => {
    if (item.comingSoon || !item.path) return;
    navigate(item.path);
  };

  return (
    <Box component="nav" aria-label="Admin primary" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Compact close affordance only — the env strip + top bar already
          announce "Admin View"; a separate "Platform Admin" band on every
          page was redundant. */}
      {onClose && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <IconButton
            onClick={onClose}
            aria-label="Close navigation"
            sx={{ color: colors.textSecondary }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
        {[...SIDEBAR_GROUPS_ADMIN]
          .sort((a, b) => a.order - b.order)
          .map((item) => {
            const active = isActive(pathname, item.path);
            const disabled = !!item.comingSoon;

            // Tooltip only rendered when disabled — MUI warns if Tooltip
            // wraps a disabled button; the inner ListItemButton uses
            // `Mui-disabled` but remains focusable via the Tooltip span.
            const button = (
              <ListItemButton
                key={item.key}
                disableRipple
                disableTouchRipple
                disabled={disabled}
                onClick={() => handleClick(item)}
                aria-current={active ? 'page' : undefined}
                sx={{
                  p: 2,
                  mb: 0.5,
                  borderRadius: '8px',
                  backgroundColor: active ? ACTIVE_BG : 'transparent',
                  border: active ? `1px solid ${ACTIVE_BORDER}` : '1px solid transparent',
                  color: colors.textPrimary,
                  gap: 1,
                  height: 48,
                  '&:hover': {
                    backgroundColor: active ? ACTIVE_BG : 'rgba(0,0,0,0.03)',
                  },
                  // Visibly "not yet" — lighter text, no hover feedback, no
                  // pointer cursor. Matches Designer A's brief for disabled
                  // items: dimmed but not invisible so super-admin still
                  // sees the feature roadmap.
                  '&.Mui-disabled': {
                    opacity: 1,
                    cursor: 'not-allowed',
                    pointerEvents: 'auto',
                    '&:hover': { backgroundColor: 'transparent' },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 24 }}>
                  <item.icon
                    sx={{
                      width: 22,
                      height: 22,
                      color: disabled
                        ? colors.textTertiary
                        : active
                          ? colors.adminAccent
                          : colors.textSecondary,
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        sx={{
                          fontSize: 15,
                          fontWeight: active ? 700 : 500,
                          color: disabled
                            ? colors.textTertiary
                            : active
                              ? colors.adminAccent
                              : colors.textPrimary,
                        }}
                      >
                        {item.label}
                      </Typography>
                      {disabled && (
                        <Box
                          aria-hidden
                          sx={{
                            fontSize: 10,
                            fontWeight: 700,
                            fontFamily: 'Manrope, sans-serif',
                            letterSpacing: '0.5px',
                            textTransform: 'uppercase',
                            color: colors.textTertiary,
                            backgroundColor: colors.bgMuted,
                            borderRadius: '4px',
                            px: 0.75,
                            py: 0.125,
                          }}
                        >
                          Soon
                        </Box>
                      )}
                    </Box>
                  }
                />
              </ListItemButton>
            );

            if (!disabled) return <Box key={item.key}>{button}</Box>;

            return (
              <Tooltip key={item.key} title="Coming soon" placement="right" arrow>
                {/* span wrapper lets Tooltip attach listeners to a disabled child */}
                <span style={{ display: 'block' }}>{button}</span>
              </Tooltip>
            );
          })}
      </Box>

      <UserBlock />
    </Box>
  );
};

export default AdminSidebar;
